import { simpleAxios } from "@/utils/http"
import Dexie from "dexie"
import type { AxiosResponse } from "axios"
import { markRaw, toRaw } from "vue"
import { useRoute } from "vue-router"
import { useIpadHuntStore } from "@/stores/apps/ipad_hunt"
import { useContentStore } from "@/stores/apps/content"
import { useEventsStore } from "@/stores/apps/events"
import { sound_alert } from "@/utils/notifications"
import { compressPlayerMediaForUpload } from "@/utils/playerMediaUpload"

/** Safari often fails storing MediaRecorder/camera Blobs in IndexedDB; ArrayBuffer is reliable. */
type QueuedFileUpload = {
	challenge: number
	team_id: number | string
	name: string
	mimeType?: string
	buffer?: ArrayBuffer
	file?: Blob
	consecutiveFailures?: number
	queueOrder?: number
}

/** Reject missing / placeholder team ids that would omit team_id in JSON or send "undefined" in FormData. */
const isValidTeamId = (teamId: unknown): boolean => {
	if (teamId == null || teamId === "") return false
	const asString = String(teamId)
	return asString !== "undefined" && asString !== "null" && asString !== "NaN"
}

/** Real Blob/File only — persisted Pinia state turns File into `{}`, which breaks multipart. */
const isValidUploadBlob = (file: unknown): file is Blob => {
	const raw = toRaw(file)
	return typeof Blob !== "undefined" && raw instanceof Blob && raw.size > 0
}

const hasQueuedUploadPayload = (entry: QueuedFileUpload | null | undefined): boolean => {
	if (!entry) return false
	if (entry.buffer instanceof ArrayBuffer && entry.buffer.byteLength > 0) return true
	return isValidUploadBlob(entry.file)
}

/** Fresh File copy so Safari/IndexedDB blobs serialize correctly into FormData. */
const toUploadFile = async (file: Blob, fileName: string): Promise<File> => {
	const raw = toRaw(file) as Blob
	const buffer = await raw.arrayBuffer()
	return new File([buffer], fileName || "upload.bin", {
		type: raw.type || "application/octet-stream",
		lastModified: Date.now(),
	})
}

const fileFromArrayBuffer = (buffer: ArrayBuffer, fileName: string, mimeType?: string): File => {
	return new File([buffer], fileName || "upload.bin", {
		type: mimeType || "application/octet-stream",
		lastModified: Date.now(),
	})
}

/** Resolve a queued upload into a real File (ArrayBuffer preferred; Blob fallback). */
const resolveQueuedUploadFile = async (entry: QueuedFileUpload): Promise<File | null> => {
	try {
		if (entry?.buffer instanceof ArrayBuffer && entry.buffer.byteLength > 0) {
			return fileFromArrayBuffer(entry.buffer, entry.name || "upload.bin", entry.mimeType)
		}
		if (isValidUploadBlob(entry?.file)) {
			return await toUploadFile(entry.file, entry.name || "upload.bin")
		}
	} catch (error) {
		console.warn("[iPadHunt] resolveQueuedUploadFile failed", { challenge: entry?.challenge, error })
	}
	return null
}

const buildSubmitFormDataFromFile = (
	teamId: unknown,
	challenge: unknown,
	uploadFile: File
): FormData => {
	const data = new FormData()
	data.append("team_id", String(teamId))
	data.append("challenge", String(challenge))
	data.append("file", uploadFile, uploadFile.name)
	return data
}

/** Allow the player to retake a challenge after a poisoned local upload queue entry. */
const clearLocalChallengeDone = async (
	challengeId: unknown,
	db: Dexie,
	ipadStore: ReturnType<typeof useIpadHuntStore>
) => {
	if (challengeId == null) return
	try {
		await db.challenge_done.delete(challengeId as number)
		await db
			.table("challenge_started")
			.where("challenge")
			.equals(challengeId)
			.modify((item: { done?: boolean }) => {
				item.done = false
			})
		await ipadStore.updateStoreChallengeToStartDb()
	} catch (error) {
		console.warn("[iPadHunt] clearLocalChallengeDone failed", { challengeId, error })
	}
}

/** Avoid concurrent updateData runs double-posting the same queue rows. */
let updateDataInFlight: Promise<void> | null = null
/** If sendFile queues while a flush is already running, run one more pass after. */
let updateDataNeedsRerun = false

/** After this many consecutive failures, move the item to the end of the queue. */
const MOVE_TO_END_AFTER_CONSECUTIVE_FAILURES = 5

type QueueAttemptFields = {
	consecutiveFailures?: number
	queueOrder?: number
}

const nextQueueOrder = (items: QueueAttemptFields[]) => {
	let max = 0
	for (const item of items) {
		const order = Number(item.queueOrder) || 0
		if (order > max) max = order
	}
	return max + 1
}

const sortByQueueOrder = <T extends QueueAttemptFields>(items: T[]): T[] => {
	return [...items].sort((a, b) => {
		const orderA = Number(a.queueOrder) || 0
		const orderB = Number(b.queueOrder) || 0
		if (orderA !== orderB) return orderA - orderB
		return 0
	})
}

/** Prefer current_team; if empty, try setTeam from the player route (same idea as PlayerIpadHunt). */
const resolveTeamId = (): number | string | null => {
	const eventStore = useEventsStore()
	const currentId = (eventStore.current_team as { id?: number | string } | null | undefined)?.id
	if (isValidTeamId(currentId)) {
		return currentId as number | string
	}
	try {
		const route = useRoute()
		const teamApiKey = route.params?.team_api_key
		if (teamApiKey == null || teamApiKey === "") {
			return null
		}
		const team = eventStore.setTeam(String(teamApiKey)) as { id?: number | string } | undefined
		const resolved = team?.id
		return isValidTeamId(resolved) ? (resolved as number | string) : null
	} catch {
		return null
	}
}

const initDB = () => {
	const db = new Dexie("Content")
	db.version(1).stores({
		challenge:
			"id, data, file1, file2, file3,match_a,match_b,match_c,match_d,match_e,match_f,match_1,match_2,match_3,match_4,match_5,match_6",
		answers: " challenge, team_id, answer",
		file_upload: "challenge, team_id, file, name",
		challenge_done: " challenge",
		challenge_started: "id, done, challenge, start, force, start_time"
	})
	return db
}

const match_images_key = ["match_a","match_b","match_c","match_d","match_e","match_f","match_1","match_2","match_3","match_4","match_5","match_6"]

const files = ["file_1_es","file_2_es","file_3_es","file_1_en","file_2_en","file_3_en"]

const IPAD_GATEWAY_BLOB_KEYS = [...files, ...match_images_key] as const

/**
 * One-time geolocation check used before opening Team WS in iPad Hunt player bootstrap.
 * QR mode skips this pre-check.
 */
const ensureIpadHuntPreTeamWsGeolocation = async (): Promise<void> => {
	const ipadStore = useIpadHuntStore()
	if ((ipadStore.ipadData as { type?: string }).type === "QR") {
		return
	}
	if (!navigator.geolocation) {
		ipadStore.modalErrorLocation = true
		return
	}
	await new Promise<void>((resolve) => {
		navigator.geolocation.getCurrentPosition(
			() => {
				ipadStore.modalErrorLocation = false
				resolve()
			},
			(error: GeolocationPositionError) => {
				if (error.code === 1) {
					ipadStore.modalErrorLocation = true
				}
				resolve()
			},
			{
				enableHighAccuracy: true,
				timeout: 15000,
				maximumAge: 10000
			}
		)
	})
}


const notifyChallengeProgress = (challengeId: number, flags: { start?: boolean; pending?: boolean }, api_key?: string) => {
	if (challengeId == -1 || !api_key) {
		return
	}
	const teamId = resolveTeamId()
	if (!isValidTeamId(teamId)) {
		return
	}
	// Fire-and-forget: must not block the player while ranking updates for the monitor
	simpleAxios("post", "IpadHunt/challenge_progress/" + api_key, {
		team_id: teamId,
		challenge: challengeId,
		start: !!flags.start,
		pending: !!flags.pending,
	}).catch(() => {})
}

// create a function to store data and remove once is upload
// also to create a function to save

const sendAnswers = async (data, challengeId, api_key?: string) => {
	if( challengeId == -1)
	{
		// IF id == -1 is that is in preview mode
		return;
	}
	const teamId = resolveTeamId()
	if (!isValidTeamId(teamId)) {
		console.warn("[iPadHunt] sendAnswers skipped: missing team_id")
		return
	}
	const ipadStore = useIpadHuntStore();

	const db = initDB()
	// eventStore.current_ipad_team.challenges.find(e => e.id == challengeId).done = true

	try{
		await db.answers.add({
			challenge: challengeId,
			answer: data,
			team_id: teamId,
			consecutiveFailures: 0,
			queueOrder: Date.now(),
		})
	}
	catch
	{
		ipadStore.sendAnswers.push({
			challenge: challengeId,
			answer: data,
			team_id: teamId,
			consecutiveFailures: 0,
			queueOrder: Date.now(),
		})
	}

	await db.challenge_done.add({
		challenge: challengeId
	})
	
	await db.table("challenge_started").where('challenge').equals(challengeId).modify(item => {
		item.done = true
	})
	ipadStore.updateStoreChallengeToStartDb();
	sound_alert("finish_challange");
	notifyChallengeProgress(challengeId, { pending: true, start: true }, api_key)

	// Flush immediately (not only QR) so pending does not sit waiting on the poller.
	if (api_key) {
		await updateData(api_key)
	}
}

const sendFile = async (data, challengeId, name, api_key?: string) => {
	if( challengeId == -1)
	{
		// IF id == -1 is that is in preview mode
		return;
	}
	const teamId = resolveTeamId()
	if (!isValidTeamId(teamId)) {
		console.warn("[iPadHunt] sendFile skipped: missing team_id")
		return
	}
	const db = initDB()
	const ipadStore = useIpadHuntStore();

	const file =
		data instanceof Blob
			? await compressPlayerMediaForUpload(data, name || "upload.bin")
			: data

	if (!isValidUploadBlob(file)) {
		console.warn("[iPadHunt] sendFile skipped: invalid file blob")
		return
	}

	const fileName = (file instanceof File ? file.name : name) || "upload.bin"
	const mimeType = file.type || "application/octet-stream"
	// markRaw: Pinia must not Proxy the Blob (breaks FormData multipart).
	const storedFile = markRaw(toRaw(file)) as Blob

	// Read bytes first: validates the blob and lets us persist ArrayBuffer (Safari-safe).
	let buffer: ArrayBuffer
	try {
		buffer = await storedFile.arrayBuffer()
	} catch (error) {
		console.warn("[iPadHunt] sendFile skipped: blob.arrayBuffer() failed", error)
		return
	}
	if (!buffer || buffer.byteLength === 0) {
		console.warn("[iPadHunt] sendFile skipped: empty buffer")
		return
	}

	const queued: QueuedFileUpload = {
		challenge: challengeId,
		team_id: teamId as number | string,
		name: fileName,
		mimeType,
		buffer,
		consecutiveFailures: 0,
		queueOrder: Date.now(),
	}

	try {
		// Persist ArrayBuffer only — storing camera/MediaRecorder Blobs crashes Safari IndexedDB
		// ("Error preparing Blob/File data to be stored in object store").
		await db.file_upload.put({
			challenge: queued.challenge,
			team_id: queued.team_id,
			name: queued.name,
			mimeType: queued.mimeType,
			buffer: queued.buffer,
			consecutiveFailures: queued.consecutiveFailures,
			queueOrder: queued.queueOrder,
		})
	} catch (error) {
		console.warn("[iPadHunt] file_upload IndexedDB put failed, using memory queue", error)
		ipadStore.sendFiles.push({
			challenge: queued.challenge,
			team_id: queued.team_id,
			name: queued.name,
			mimeType: queued.mimeType,
			buffer: queued.buffer,
			file: storedFile,
			consecutiveFailures: queued.consecutiveFailures,
			queueOrder: queued.queueOrder,
		})
	}

	try {
		await db.challenge_done.put({
			challenge: challengeId
		})
		await db.table("challenge_started").where('challenge').equals(challengeId).modify(item => {
			item.done = true
		})
	} catch (error) {
		console.warn("[iPadHunt] challenge_done mark failed", error)
	}
	ipadStore.updateStoreChallengeToStartDb();
	sound_alert("finish_challange");
	notifyChallengeProgress(challengeId, { pending: true, start: true }, api_key)

	// Always flush now — waiting only on the 3s poller leaves amber "Uploading" stuck on reload.
	if (api_key) {
		await updateData(api_key)
	}
}

const getChallengeDone = async () => {
	const db = initDB()
	const data = await db.challenge_done.toArray()

	return data.map(obj => obj.challenge)
}


/** SubmitAnswer returns HTTP 200 even when scoring is "incorrect"; only "missing file" should keep the local retry queue. */
const shouldClearSubmitQueue = (response: AxiosResponse) => {
	return response?.data?.data !== "missing file"
}

/** Drop poisoned queue rows (no team_id / no real file) so the retry loop cannot flood the API. */
const dropInvalidQueuedSubmit = async (
	kind: "answers" | "file_upload" | "sendAnswers" | "sendFiles",
	challenge: unknown,
	ipadStore: ReturnType<typeof useIpadHuntStore>,
	db: Dexie,
	index?: number
) => {
	console.warn("[iPadHunt] dropping invalid queued submit", { kind, challenge })
	if (kind === "answers") {
		await db.answers.delete(challenge as number)
		return
	}
	if (kind === "file_upload") {
		await db.file_upload.delete(challenge as number)
		return
	}
	if (kind === "sendAnswers" && index != null) {
		ipadStore.sendAnswers.splice(index, 1)
		return
	}
	if (kind === "sendFiles" && index != null) {
		ipadStore.sendFiles.splice(index, 1)
	}
}

/** Only drop on 400 when local payload is actually bad — empty-body 400s must retry. */
const shouldDropOnClientError = (error: any, localValid: boolean) => {
	if (error?.response?.status !== 400) return false
	if (localValid) return false
	return true
}

type SubmitAttemptResult = "cleared" | "retry" | "drop_invalid"

const recordDbSubmitFailure = async (
	table: "answers" | "file_upload",
	db: Dexie,
	item: QueueAttemptFields & { challenge: number },
	allItems: QueueAttemptFields[]
) => {
	const consecutiveFailures = (Number(item.consecutiveFailures) || 0) + 1
	const patch: QueueAttemptFields = {
		consecutiveFailures,
		queueOrder: Number(item.queueOrder) || 0,
	}

	if (consecutiveFailures >= MOVE_TO_END_AFTER_CONSECUTIVE_FAILURES) {
		patch.consecutiveFailures = 0
		patch.queueOrder = nextQueueOrder(allItems)
		console.warn("[iPadHunt] moving failed submit to end of queue", {
			table,
			challenge: item.challenge,
			queueOrder: patch.queueOrder,
		})
	}

	item.consecutiveFailures = patch.consecutiveFailures
	item.queueOrder = patch.queueOrder
	await db.table(table).update(item.challenge, patch)
}

const recordMemorySubmitFailure = (
	list: Array<QueueAttemptFields & { challenge?: unknown }>,
	index: number
): "kept" | "rotated" => {
	const item = list[index]
	if (!item) return "kept"

	const consecutiveFailures = (Number(item.consecutiveFailures) || 0) + 1
	item.consecutiveFailures = consecutiveFailures

	if (consecutiveFailures >= MOVE_TO_END_AFTER_CONSECUTIVE_FAILURES) {
		item.consecutiveFailures = 0
		item.queueOrder = nextQueueOrder(list)
		const [moved] = list.splice(index, 1)
		list.push(moved)
		console.warn("[iPadHunt] moving memory failed submit to end of queue", {
			challenge: moved.challenge,
			queueOrder: moved.queueOrder,
		})
		return "rotated"
	}

	return "kept"
}

const trySubmitAnswerPayload = async (
	api_key_event: string,
	payload: { challenge: unknown; answer: unknown; team_id: unknown },
	localValid: boolean
): Promise<SubmitAttemptResult> => {
	try {
		const response = await simpleAxios("post", "IpadHunt/submit_answer/" + api_key_event, {
			challenge: payload.challenge,
			answer: payload.answer,
			team_id: payload.team_id,
		})
		if (!shouldClearSubmitQueue(response)) return "retry"
		return "cleared"
	} catch (error: any) {
		if (shouldDropOnClientError(error, localValid)) return "drop_invalid"
		return "retry"
	}
}

const trySubmitFilePayload = async (
	api_key_event: string,
	entry: QueuedFileUpload,
	localValid: boolean
): Promise<SubmitAttemptResult> => {
	try {
		const uploadFile = await resolveQueuedUploadFile(entry)
		if (!uploadFile) return "drop_invalid"
		const data = buildSubmitFormDataFromFile(entry.team_id, entry.challenge, uploadFile)
		const response = await simpleAxios("post", "IpadHunt/submit_answer/" + api_key_event, data)
		if (!shouldClearSubmitQueue(response)) return "retry"
		return "cleared"
	} catch (error: any) {
		if (shouldDropOnClientError(error, localValid)) return "drop_invalid"
		return "retry"
	}
}

/**
 * Recover iPads stuck on amber "Uploading": server has pending=true but the local
 * file queue was lost (Safari IndexedDB Blob failure + reload/rotate).
 * Clears local challenge_done so the player can retake and resubmit.
 */
const reconcileOrphanedPendingUploads = async (
	db: Dexie,
	ipadStore: ReturnType<typeof useIpadHuntStore>
) => {
	try {
		const eventStore = useEventsStore()
		const serverChallenges =
			(eventStore.current_ipad_team as { challenges?: Array<{ challenge: number; pending?: boolean; done?: boolean }> } | null)
				?.challenges || []
		if (!serverChallenges.length) return

		const [doneRows, queuedFiles, queuedAnswers] = await Promise.all([
			db.challenge_done.toArray(),
			db.file_upload.toArray(),
			db.answers.toArray(),
		])
		const queued = new Set<number>([
			...queuedFiles.map((e: { challenge: number }) => e.challenge),
			...queuedAnswers.map((e: { challenge: number }) => e.challenge),
			...ipadStore.sendFiles.map((e: { challenge: number }) => e.challenge),
			...ipadStore.sendAnswers.map((e: { challenge: number }) => e.challenge),
		])

		for (const row of doneRows as Array<{ challenge: number }>) {
			const challengeId = row?.challenge
			if (challengeId == null || queued.has(challengeId)) continue
			const server = serverChallenges.find((c) => c.challenge == challengeId)
			if (server && server.pending && !server.done) {
				console.warn("[iPadHunt] orphaned pending upload — clearing local done for resubmit", challengeId)
				await clearLocalChallengeDone(challengeId, db, ipadStore)
			}
		}
	} catch (error) {
		console.warn("[iPadHunt] reconcileOrphanedPendingUploads failed", error)
	}
}

const updateData = async (api_key_event: string) => {
	if (updateDataInFlight) {
		// A send can land while a flush is mid-loop; ensure we process the new rows after.
		updateDataNeedsRerun = true
		return updateDataInFlight
	}

	updateDataInFlight = (async () => {
		do {
			updateDataNeedsRerun = false
			const db = initDB()
			const ipadStore = useIpadHuntStore()
			await reconcileOrphanedPendingUploads(db, ipadStore)

			// IndexedDB answers — failures must not block later items in the queue.
			const answers = sortByQueueOrder(await db.answers.toArray())
			for (const e of answers) {
				const localValid = isValidTeamId(e?.team_id) && e?.challenge != null
				if (!localValid) {
					await dropInvalidQueuedSubmit("answers", e?.challenge, ipadStore, db)
					continue
				}

				const result = await trySubmitAnswerPayload(
					api_key_event,
					{ challenge: e.challenge, answer: e.answer, team_id: e.team_id },
					localValid
				)
				if (result === "cleared") {
					await db.answers.delete(e.challenge)
				} else if (result === "drop_invalid") {
					await dropInvalidQueuedSubmit("answers", e.challenge, ipadStore, db)
				} else {
					await recordDbSubmitFailure("answers", db, e, answers)
				}
			}

			// IndexedDB file uploads (ArrayBuffer preferred; legacy Blob still supported)
			const file_upload = sortByQueueOrder(
				(await db.file_upload.toArray()) as QueuedFileUpload[]
			)
			for (const e of file_upload) {
				const localValid =
					isValidTeamId(e?.team_id) && e?.challenge != null && hasQueuedUploadPayload(e)
				if (!localValid) {
					await dropInvalidQueuedSubmit("file_upload", e?.challenge, ipadStore, db)
					await clearLocalChallengeDone(e?.challenge, db, ipadStore)
					continue
				}

				const result = await trySubmitFilePayload(api_key_event, e, localValid)
				if (result === "cleared") {
					await db.file_upload.delete(e.challenge)
				} else if (result === "drop_invalid") {
					await dropInvalidQueuedSubmit("file_upload", e.challenge, ipadStore, db)
					await clearLocalChallengeDone(e.challenge, db, ipadStore)
				} else {
					await recordDbSubmitFailure("file_upload", db, e, file_upload)
				}
			}

			// In-memory answer fallback queue (process front-to-back; rotate failures to end)
			ipadStore.sendAnswers = sortByQueueOrder(ipadStore.sendAnswers)
			for (let index = 0; index < ipadStore.sendAnswers.length; ) {
				const e = ipadStore.sendAnswers[index]
				const localValid = isValidTeamId(e?.team_id) && e?.challenge != null
				if (!localValid) {
					await dropInvalidQueuedSubmit("sendAnswers", e?.challenge, ipadStore, db, index)
					continue
				}

				const result = await trySubmitAnswerPayload(
					api_key_event,
					{ challenge: e.challenge, answer: e.answer, team_id: e.team_id },
					localValid
				)
				if (result === "cleared") {
					ipadStore.sendAnswers.splice(index, 1)
					continue
				}
				if (result === "drop_invalid") {
					await dropInvalidQueuedSubmit("sendAnswers", e.challenge, ipadStore, db, index)
					continue
				}

				const failureAction = recordMemorySubmitFailure(ipadStore.sendAnswers, index)
				if (failureAction === "kept") {
					index += 1
				}
				// rotated/dropped: next item is already at this index
			}

			// In-memory file fallback queue
			ipadStore.sendFiles = sortByQueueOrder(ipadStore.sendFiles)
			for (let index = 0; index < ipadStore.sendFiles.length; ) {
				const e = ipadStore.sendFiles[index] as QueuedFileUpload
				const localValid =
					isValidTeamId(e?.team_id) && e?.challenge != null && hasQueuedUploadPayload(e)
				if (!localValid) {
					await dropInvalidQueuedSubmit("sendFiles", e?.challenge, ipadStore, db, index)
					await clearLocalChallengeDone(e?.challenge, db, ipadStore)
					continue
				}

				const result = await trySubmitFilePayload(api_key_event, e, localValid)
				if (result === "cleared") {
					ipadStore.sendFiles.splice(index, 1)
					continue
				}
				if (result === "drop_invalid") {
					await dropInvalidQueuedSubmit("sendFiles", e.challenge, ipadStore, db, index)
					await clearLocalChallengeDone(e.challenge, db, ipadStore)
					continue
				}

				const failureAction = recordMemorySubmitFailure(ipadStore.sendFiles, index)
				if (failureAction === "kept") {
					index += 1
				}
			}
		} while (updateDataNeedsRerun)
	})().finally(() => {
		updateDataInFlight = null
		updateDataNeedsRerun = false
	})

	return updateDataInFlight
}

function degreesToRadians(degrees) {
	return (degrees * Math.PI) / 180
}

function haversineDistance(lat1, lon1, lat2, lon2) {
	// Convert decimal degrees to radians
	lat1 = degreesToRadians(lat1)
	lon1 = degreesToRadians(lon1)
	lat2 = degreesToRadians(lat2)
	lon2 = degreesToRadians(lon2)

	// Haversine formula
	const dlon = lon2 - lon1
	const dlat = lat2 - lat1
	const a = Math.pow(Math.sin(dlat / 2), 2) + Math.cos(lat1) * Math.cos(lat2) * Math.pow(Math.sin(dlon / 2), 2)
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
	const radiusOfEarth = 6371 // Radius of the Earth in kilometers
	const distance = radiusOfEarth * c * 1000 // Convert to meters
	return distance
}

function isPointInsideCircle(centerLat, centerLon, pointLat, pointLon, radius) {
	// Calculate the distance between the center point and the given point
	const distance = haversineDistance(centerLat, centerLon, pointLat, pointLon)

	// Check if the distance is less than or equal to the radius
	return distance <= radius
}


const deleteAllcontent = async () => {
	const dbs = await window.indexedDB.databases()
	dbs.forEach(db => {
		window.indexedDB.deleteDatabase(db.name)
	})
	location.reload()
}

/**
 * Clears only the player-progress tables (answers, file_upload, challenge_done,
 * challenge_started) without touching cached media or reloading the page.
 * Call this when demo mode is stopped so the next demo cycle starts clean.
 */
const clearDemoProgressDb = async () => {
	try {
		const db = initDB()
		await db.open()
		await Promise.all([
			db.table("answers").clear(),
			db.table("file_upload").clear(),
			db.table("challenge_done").clear(),
			db.table("challenge_started").clear(),
		])
	} catch (e) {
		console.warn("[iPadHunt] clearDemoProgressDb error:", e)
	}
}

const activeChallenge = async (data, api_key?: string) => {
	// Ensure we have at most 1 row per challenge in challenge_started.
	const db = initDB()
	const challengeId = data?.id
	// content id used to query `challenge` table
	// In some flows `data.challenge` can be undefined if we changed data shape.
	const contentId =
		data?.challenge ??
		data?.contentId ??
		challengeId

	// If already completed locally, don't re-create a started challenge.
	if (challengeId === undefined || challengeId === null) {
		console.warn("[iPadHunt] activeChallenge missing id", data)
		return
	}
	if (contentId === undefined || contentId === null) {
		console.warn("[iPadHunt] activeChallenge missing contentId", data)
		return
	}

	const doneRow = await db.challenge_done.get(challengeId)
	if (doneRow) {
		const ipadStore = useIpadHuntStore()
		ipadStore.updateStoreChallengeToStartDb()
		return
	}

	const existing = await db.challenge_started.get(challengeId)
	if (existing && existing.done === true) {
		const ipadStore = useIpadHuntStore()
		ipadStore.updateStoreChallengeToStartDb()
		return
	}

	const content = await db.challenge.where("id").equals(contentId).first()
	const currentDate = new Date()
	const seconds = content?.data?.time ?? 60
	const futureDate = new Date(currentDate.getTime() + seconds * 1000)

	// Use deterministic primary key id = challengeId, and put (upsert) to avoid duplicates.
	await db.challenge_started.put({
		id: challengeId,
		done: false,
		challenge: challengeId,
		start: true,
		force: false,
		start_time: futureDate,
	})

	const ipadStore = useIpadHuntStore()
	ipadStore.updateStoreChallengeToStartDb()
	notifyChallengeProgress(challengeId, { start: true }, api_key)
}

const getActiveChallenge = async()=>{
	// create new challenge data.
	const db = initDB()
	return await db.challenge_started.toArray()
} 

const dumpIndexedDbContent = async () => {
	const db = initDB()
	const tableNames = ["challenge", "answers", "file_upload", "challenge_done", "challenge_started"]

	const toLogSafe = (value: any) => {
		if (value instanceof Blob) {
			return { __type: "Blob", type: value.type, size: value.size }
		}
		if (value instanceof ArrayBuffer) {
			return { __type: "ArrayBuffer", byteLength: value.byteLength }
		}
		return value
	}

	const sanitize = (row: any) => {
		if (row == null || typeof row !== "object") return row
		if (Array.isArray(row)) return row.map(sanitize)

		const out: Record<string, any> = {}
		for (const [k, v] of Object.entries(row)) out[k] = sanitize(toLogSafe(v))
		return out
	}

	console.groupCollapsed("[iPadHunt][IndexedDB] Dexie DB: Content")
	for (const name of tableNames) {
		try {
			const rows = await db.table(name).toArray()
			console.groupCollapsed(`[iPadHunt][IndexedDB] table: ${name} (${rows.length})`)
			console.log(rows.map(sanitize))
			console.groupEnd()
		} catch (e) {
			console.warn(`[iPadHunt][IndexedDB] failed reading table: ${name}`, e)
		}
	}
	console.groupEnd()
}



export {
	ensureIpadHuntPreTeamWsGeolocation,
	sendAnswers,
	sendFile,
	updateData,
	getChallengeDone,
	isPointInsideCircle,
	deleteAllcontent, 
	activeChallenge, 
	getActiveChallenge,
	dumpIndexedDbContent,
	clearDemoProgressDb,
}
